import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useUserAutoLoginActionTokenMutation } from '../../libs/graphql/baseAppAPI/mutations/__generated__/user-auto-login-action-token.mutation.generated';
import { xTenantId } from '../../consts';
import { clearOTAT, extractAutoLoginParams, AutoLoginParams } from '../../utils/auto-login';
import { config } from '../../components/Authentification/config';
import { trackEvent } from '../../tracking/tracking';

interface UseAutoLoginReturn {
  isAutoLoginInProgress: boolean;
  autoLoginError: string | null;
}

export const useAutoLogin = (): UseAutoLoginReturn => {
  const auth = useAuth();
  const [userAutoLoginActionTokenMutation] = useUserAutoLoginActionTokenMutation();
  const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState(false);
  const [autoLoginError, setAutoLoginError] = useState<string | null>(null);

  const getAutoLoginParams = (): AutoLoginParams | null => {
    const params = extractAutoLoginParams();

    if (!params) {
      return null;
    }

    // Use config as fallback for KC context parameters
    return {
      ...params,
      realm: params.realm || config.realm,
      clientId: params.clientId || config.clientId,
    };
  };

  useEffect(() => {
    const handleAutoLogin = async (): Promise<void> => {
      const autoLoginParams = getAutoLoginParams();

      if (!autoLoginParams) {
        return;
      }

      setIsAutoLoginInProgress(true);
      setAutoLoginError(null);

      // Get the OTAT from subdomain-shared cookie

      if (!autoLoginParams.oneTimeAuthToken) {
        const error = 'No OTAT found in cookie for auto-login';

        console.error(error);
        try {
          trackEvent({ event: 'auto_login_failure', reason: error });
        } catch (trackingError) {
          console.error('Auto login failure tracking failed:', trackingError);
        }

        setAutoLoginError(error);
        setIsAutoLoginInProgress(false);

        return;
      }

      try {
        let actionTokenUrl = null;
        let attempts = 0;

        while (!actionTokenUrl && attempts < 3) {
          attempts++;

          const result = await userAutoLoginActionTokenMutation({
            variables: {
              xTenantId: xTenantId,
              userAutoLoginActionTokenInput: {
                clientId: autoLoginParams.clientId,
                codeChallenge: autoLoginParams.code_challenge,
                feBaseUrl: `${window.location.origin}`,
                oneTimeAuthToken: autoLoginParams.oneTimeAuthToken,
                realm: autoLoginParams.realm,
                redirectUri: `${autoLoginParams.redirectUri}`,
                state: autoLoginParams.state,
                userProfileId: autoLoginParams.profileID,
              },
            },
          });

          console.log(`Attempt ${attempts}:`, JSON.stringify(result, undefined, 2));

          if (result.data?.userAutoLoginActionToken?.data) {
            actionTokenUrl = result.data.userAutoLoginActionToken.data;
          } else if (attempts < 3) {
            // Wait before retry: 1s for first retry, 2s for second retry
            const delay = attempts * 1000;

            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }

        if (actionTokenUrl) {
          // Clear the OTAT as it's single-use
          clearOTAT();

          try {
            trackEvent({ event: 'auto_login_success' });
          } catch (trackingError) {
            console.error('Auto login success tracking failed:', trackingError);
          }

          // Redirect to the action token URL
          window.location.href = actionTokenUrl;
        } else {
          const error = 'Auto-login action token failed - no URL returned after 3 attempts';

          console.error(error);
          try {
            trackEvent({ event: 'auto_login_failure', reason: error });
          } catch (trackingError) {
            console.error('Auto login failure tracking failed:', trackingError);
          }

          setAutoLoginError(error);
          clearOTAT();
          setIsAutoLoginInProgress(false);
        }
      } catch (error) {
        const errorMessage = `Auto-login failed: ${error}`;

        console.error(errorMessage);
        try {
          trackEvent({ event: 'auto_login_failure', reason: errorMessage });
        } catch (trackingError) {
          console.error('Auto login failure tracking failed:', trackingError);
        }

        setAutoLoginError(errorMessage);
        // Clear the OTAT on error
        clearOTAT();
        setIsAutoLoginInProgress(false);
      }
    };

    // Only run when auth is ready and not loading
    if (auth && !auth.isLoading) {
      handleAutoLogin();
    }
  }, [auth, userAutoLoginActionTokenMutation]);

  return {
    isAutoLoginInProgress,
    autoLoginError,
  };
};

// Helper function to check if URL contains auto-login parameters
export const isAutoLoginUrl = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);

  return urlParams.get('autoLogin') === 'true';
};
